import { isEngineeringUser, useAuth } from "@assetwatch/auth";
import {
  AppShell,
  AppShellMobileNavigationTrigger,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  DEFAULT_PROFILE_MENU_LABELS,
  ProfileMenu,
  Sidebar,
  SidebarItem,
  TopBar,
  TopBarActions,
  TopBarContent,
  TopBarLeading,
} from "@assetwatch/ui";
import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { MarketplaceBrandLabel } from "../components/MarketplaceBrandLabel";
import { getLegacyCustomerTabUrl } from "../lib/legacy-app-url";

function MarketplaceLayout() {
  const { t } = useTranslation();
  const { signOut, user } = useAuth();
  const pathname = useRouterState({
    select: (state) =>
      state.resolvedLocation?.pathname ?? state.location.pathname,
  });
  const { sourceApp, externalCustomerId, facilityIds } = useRouterState({
    select: (state) =>
      state.location.search as {
        sourceApp?: "legacy" | "enterprise" | "facility";
        externalCustomerId?: string | number;
        facilityIds?: string | number;
      },
  });
  const showLegacyNavigation =
    sourceApp === "legacy" && Boolean(externalCustomerId);
  const legacyCustomerId = String(externalCustomerId ?? "");
  const legacyFacilityIds =
    facilityIds === undefined ? undefined : String(facilityIds);

  return (
    <AppShell>
      <TopBar>
        <TopBarLeading>
          <AppShellMobileNavigationTrigger />
          <span className="max-[599px]:hidden">
            <MarketplaceBrandLabel />
          </span>
        </TopBarLeading>
        <TopBarContent>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t("common.home")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </TopBarContent>
        <TopBarActions>
          <ProfileMenu
            engineeringOnly={isEngineeringUser(user)}
            labels={DEFAULT_PROFILE_MENU_LABELS}
            onSignOut={() => void signOut()}
          />
        </TopBarActions>
      </TopBar>
      <Sidebar mobileHeaderLeading={<MarketplaceBrandLabel />}>
        <SidebarItem
          itemId="home"
          label={t("common.home")}
          leading={{ type: "icon", name: "home" }}
          render={<Link to="/" />}
          selected={pathname === "/"}
        />
        {showLegacyNavigation
          ? [
              ["summary", "summary"],
              ["facility-layout", "facilityLayout"],
              ["sensors", "sensors"],
              ["hubs", "hubs"],
              ["hotspots", "hotspots"],
              ["oil", "oil"],
              ["users", "users"],
              ["settings", "settings"],
            ].map(([tab, labelKey]) => (
              <SidebarItem
                key={tab}
                itemId={`legacy-${tab}`}
                label={t(`marketplace.legacyNavigation.${labelKey}`)}
                href={getLegacyCustomerTabUrl(
                  legacyCustomerId,
                  tab,
                  legacyFacilityIds,
                )}
                target="_self"
              />
            ))
          : null}
      </Sidebar>
      <Outlet />
    </AppShell>
  );
}

export const Route = createFileRoute("/_app")({
  component: MarketplaceLayout,
});
